/**
 * Variant Option + Product Metafield Filter
 *
 * Shopify storefront filtering narrows collection.products to products that have
 * *at least one* matching variant, but product.variants always returns all
 * variants. This script hides non-matching grid rows using data attributes
 * stamped by Liquid.
 *
 * Option filters (filter.v.option.*):
 *   Variant rows: data-opt-name-0..2, data-opt-value-0..2
 *   Product rows: data-cpf-product + data-cpf-variants JSON + option names
 *
 * Metafield filters (filter.p.m.*):
 *   data-cpf-pmf JSON map: full param name -> metafield .value
 */
(function () {
  'use strict';

  var win = /** @type {any} */ (window);
  if (win.__variantOptionFilterLoaded) return;
  win.__variantOptionFilterLoaded = true;

  var FILTER_PREFIX = 'filter.v.option.';
  var METAFIELD_PREFIX = 'filter.p.m.';

  /**
   * @param {string | null | undefined} s
   */
  function normalizeFilterToken(s) {
    if (s == null || s === '') return '';
    return String(s)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  /**
   * @param {URLSearchParams} params
   * @returns {{ [optNameNormalized: string]: string[] }}
   */
  function getActiveOptionFilters(params) {
    /** @type {{ [optName: string]: string[] }} */
    var filters = {};
    params.forEach(function (value, key) {
      if (key.startsWith(FILTER_PREFIX)) {
        var rawName = key.slice(FILTER_PREFIX.length);
        var optNameNorm = normalizeFilterToken(rawName);
        if (!optNameNorm) return;
        var optBucket = filters[optNameNorm];
        if (!optBucket) {
          optBucket = [];
          filters[optNameNorm] = optBucket;
        }
        optBucket.push(value.trim());
      }
    });
    return filters;
  }

  /**
   * @param {URLSearchParams} params
   * @returns {{ [paramName: string]: string[] }}
   */
  function getActiveMetafieldFilters(params) {
    /** @type {{ [paramName: string]: string[] }} */
    var filters = {};
    params.forEach(function (value, key) {
      if (key.startsWith(METAFIELD_PREFIX)) {
        var mfBucket = filters[key];
        if (!mfBucket) {
          mfBucket = [];
          filters[key] = mfBucket;
        }
        mfBucket.push(value.trim());
      }
    });
    return filters;
  }

  /**
   * @param {string[]} allowedValues
   * @param {string | undefined} variantValue
   */
  function valueMatchesAllowedNormalized(allowedValues, variantValue) {
    if (variantValue === undefined || variantValue === null) return false;
    var nv = normalizeFilterToken(variantValue);
    for (var a = 0; a < allowedValues.length; a++) {
      if (normalizeFilterToken(allowedValues[a]) === nv) return true;
    }
    return false;
  }

  /**
   * @param {string[]} allowedValues
   * @param {unknown} productVal
   */
  function metafieldValueMatches(allowedValues, productVal) {
    var normalizedAllowed = [];
    for (var a = 0; a < allowedValues.length; a++) {
      normalizedAllowed.push(normalizeFilterToken(allowedValues[a]));
    }

    if (productVal == null || productVal === '') {
      for (var i = 0; i < normalizedAllowed.length; i++) {
        if (normalizedAllowed[i] === '') return true;
      }
      return false;
    }

    if (Array.isArray(productVal)) {
      for (var p = 0; p < productVal.length; p++) {
        var nv = normalizeFilterToken(String(productVal[p]));
        for (var j = 0; j < normalizedAllowed.length; j++) {
          if (normalizedAllowed[j] === nv) return true;
        }
      }
      return false;
    }

    if (typeof productVal === 'object') {
      try {
        return metafieldValueMatches(allowedValues, JSON.stringify(productVal));
      } catch (e) {
        return false;
      }
    }

    var pv = normalizeFilterToken(String(productVal));
    for (var k = 0; k < normalizedAllowed.length; k++) {
      if (normalizedAllowed[k] === pv) return true;
    }
    return false;
  }

  /**
   * @param {HTMLElement} li
   * @param {{ [paramName: string]: string[] }} mfFilters
   */
  function passesMetafieldFilters(li, mfFilters) {
    var keys = Object.keys(mfFilters);
    if (keys.length === 0) return true;

    var raw = li.getAttribute('data-cpf-pmf');
    if (raw == null || raw === '') {
      return false;
    }

    /** @type {Record<string, unknown>} */
    var map;
    try {
      map = JSON.parse(raw);
    } catch (e) {
      return false;
    }

    for (var k = 0; k < keys.length; k++) {
      var paramKey = keys[k];
      if (!paramKey) continue;
      var allowed = mfFilters[paramKey];
      if (!allowed) continue;
      var productVal = /** @type {Record<string, unknown>} */ (map)[paramKey];
      if (!metafieldValueMatches(allowed, productVal)) {
        return false;
      }
    }
    return true;
  }

  /**
   * @param {HTMLElement} li
   * @param {{ [optName: string]: string[] }} optionFilters
   */
  function passesOptionVariantRow(li, optionFilters) {
    var filterKeys = Object.keys(optionFilters);
    if (filterKeys.length === 0) return true;

    for (var f = 0; f < filterKeys.length; f++) {
      var filterOptName = filterKeys[f];
      if (!filterOptName) continue;
      var allowedValues = optionFilters[filterOptName];
      if (!allowedValues) continue;
      var matchedDim = false;

      for (var i = 0; i < 3; i++) {
        var storedName = li.getAttribute('data-opt-name-' + i);
        if (storedName === null || storedName === '') continue;
        const filterName = window.filterOptions[filterOptName] ? window.filterOptions[filterOptName] : filterOptName;
        if (normalizeFilterToken(storedName) !== filterName) continue;

        matchedDim = true;
        var variantValue = li.getAttribute('data-opt-value-' + i);
        if (!valueMatchesAllowedNormalized(allowedValues, variantValue === null ? undefined : variantValue)) {
          return false;
        }
        break;
      }

      if (!matchedDim) {
        return false;
      }
    }
    return true;
  }

  /**
   * @param {HTMLElement} li
   * @param {{ [optName: string]: string[] }} optionFilters
   */
  function passesOptionProductCard(li, optionFilters) {
    var filterKeys = Object.keys(optionFilters);
    if (filterKeys.length === 0) return true;

    var raw = li.getAttribute('data-cpf-variants');
    if (!raw) {
      return false;
    }

    /** @type {{ p: number, o: string[] }[]} */
    var variants;
    try {
      variants = JSON.parse(raw);
    } catch (e) {
      return false;
    }

    return variants.some(function (v) {
      if (!v || !v.o) return false;

      for (var fk = 0; fk < filterKeys.length; fk++) {
        var filterOptName = filterKeys[fk];
        if (!filterOptName) continue;
        var allowedValues = optionFilters[filterOptName];
        if (!allowedValues) return false;
        var dimOk = false;

        for (var i = 0; i < 3; i++) {
          var storedName = li.getAttribute('data-opt-name-' + i);
          if (storedName === null || storedName === '') continue;
          if (normalizeFilterToken(storedName) !== filterOptName) continue;

          var val = v.o[i];
          dimOk = valueMatchesAllowedNormalized(allowedValues, val != null ? String(val) : undefined);
          break;
        }

        if (!dimOk) return false;
      }
      return true;
    });
  }

  /**
   * @param {URLSearchParams} params
   */
  function applyAllFilters(params) {
    var optionFilters = getActiveOptionFilters(params);
    var mfFilters = getActiveMetafieldFilters(params);
    var hasAnyOption = Object.keys(optionFilters).length > 0;
    var hasAnyMf = Object.keys(mfFilters).length > 0;

    document.querySelectorAll('.product-grid__item[data-variant-id]').forEach(function (el) {
      var li = /** @type {HTMLElement} */ (el);
      var passOpt = !hasAnyOption || passesOptionVariantRow(li, optionFilters);
      var passMf = !hasAnyMf || passesMetafieldFilters(li, mfFilters);
      li.style.display = passOpt && passMf ? '' : 'none';
    });

    document.querySelectorAll('.product-grid__item[data-cpf-product]').forEach(function (el) {
      var li = /** @type {HTMLElement} */ (el);
      var passOpt = !hasAnyOption || passesOptionProductCard(li, optionFilters);
      var passMf = !hasAnyMf || passesMetafieldFilters(li, mfFilters);
      li.style.display = passOpt && passMf ? '' : 'none';
    });
  }

  function applyFiltersFromURL() {
    applyAllFilters(new URLSearchParams(window.location.search));
  }

  /** @type {URLSearchParams | null} */
  var pendingParams = null;

  document.addEventListener('filter:update', function (event) {
    var ce = /** @type {CustomEvent} */ (event);
    var qp = ce.detail && ce.detail.queryParams;
    if (qp) {
      pendingParams = qp;
      applyAllFilters(qp);
    }
  });

  /** @type {ReturnType<typeof setTimeout> | null} */
  var debounceTimer = null;

  var observer = new MutationObserver(function () {
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      var pp = pendingParams;
      if (pp !== null) {
        applyAllFilters(pp);
      } else {
        applyFiltersFromURL();
      }
    }, 30);
  });

  function startObserving() {
    var roots = [
      document.getElementById('ResultsList'),
      document.querySelector('.product-grid-container'),
      document.querySelector('.cpf-main'),
    ]
      .concat(Array.prototype.slice.call(document.querySelectorAll('.cp-layout .cp-main')))
      .filter(function (n, i, a) {
        return n && a.indexOf(n) === i;
      });

    for (var r = 0; r < roots.length; r++) {
      var node = roots[r];
      if (node) observer.observe(node, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      applyFiltersFromURL();
      startObserving();
    });
  } else {
    applyFiltersFromURL();
    startObserving();
  }

  window.addEventListener('popstate', function () {
    applyFiltersFromURL();
  });
})();
